import React, { useRef, useState } from "react";
import { Play, VideoCamera } from "@phosphor-icons/react";

// Extrae el ID de un video de YouTube desde distintas variantes de URL.
export function extractYouTubeId(url = "") {
  if (!url) return null;
  const patterns = [
    /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/))([^#&?]{11})/i,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
}

// Detecta automáticamente el tipo de media basándose en la URL.
export function detectMediaType(url = "") {
  if (!url) return "image";
  const u = url.toLowerCase();
  if (extractYouTubeId(u)) return "youtube";
  if (u.endsWith(".gif")) return "gif";
  if (u.match(/\.(mp4|webm|mov|m4v|ogg)(\?|$)/)) return "video";
  return "image";
}

/**
 * Renderiza el media de un ejercicio según tipo detectado o declarado.
 * - image/gif: <img>
 * - video: <video> autoplay muted loop on hover
 * - youtube: thumbnail clickable (abre iframe en modal)
 *
 * Nota: si `video_url` existe pero NO es un video reproducible directo
 * (por ej. link de búsqueda YouTube), se usa `image_url` como preview
 * y se ofrece un botón para abrir el video_url en una nueva pestaña.
 */
export default function ExerciseMedia({ exercise, className = "", autoHover = true, size = "md" }) {
  const { image_url, video_url } = exercise || {};
  const declaredType = exercise?.media_type;

  const ytId = extractYouTubeId(video_url);
  const isVideoFile = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(video_url || "");
  // El video_url solo es "renderable" (playable) si es un YouTube VIDEO
  // específico (11-char ID) o un archivo de video con extensión conocida.
  const videoRenderable = !!(ytId || isVideoFile);
  const primaryUrl = videoRenderable ? video_url : (image_url || video_url);
  const type = declaredType && declaredType !== "image"
    ? declaredType
    : (videoRenderable ? detectMediaType(video_url) : "image");
  // Link externo a un video no-embebible (ej: YouTube search URL). Se muestra
  // como botón overlay para que el usuario acceda al recurso.
  const externalVideoLink = !videoRenderable && video_url ? video_url : null;

  const [playingYt, setPlayingYt] = useState(false);
  const videoRef = useRef(null);

  const handleMouseEnter = () => {
    if (!autoHover || type !== "video") return;
    videoRef.current?.play?.().catch(() => {});
  };
  const handleMouseLeave = () => {
    if (!autoHover || type !== "video") return;
    videoRef.current?.pause?.();
    if (videoRef.current) videoRef.current.currentTime = 0;
  };

  if (!primaryUrl) {
    return (
      <div className={`w-full h-full flex items-center justify-center text-muted text-xs bg-[#0A0A0A] ${className}`}>
        Sin media
      </div>
    );
  }

  if (type === "youtube") {
    const id = extractYouTubeId(primaryUrl);
    if (!id) {
      return <div className={`w-full h-full flex items-center justify-center text-muted text-xs bg-[#0A0A0A] ${className}`}>URL inválida</div>;
    }
    if (playingYt) {
      return (
        <iframe
          className={`w-full h-full ${className}`}
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`}
          title={exercise?.name || "YouTube"}
          frameBorder="0"
          allow="accelerated-sensors; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setPlayingYt(true); }}
        className={`relative w-full h-full block group ${className}`}
        data-testid="youtube-play-btn"
      >
        <img
          src={`https://img.youtube.com/vi/${id}/${size === "sm" ? "mqdefault" : "hqdefault"}.jpg`}
          alt={exercise?.name || ""}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
          <div className="w-14 h-14 rounded-full bg-[#FF3B30] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play size={24} weight="fill" className="text-white translate-x-0.5" />
          </div>
        </div>
        <div className="absolute top-2 left-2 text-[9px] uppercase tracking-widest font-bold bg-black/80 text-white px-1.5 py-0.5">
          YouTube
        </div>
      </button>
    );
  }

  if (type === "video") {
    return (
      <div className={`relative w-full h-full ${className}`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <video
          ref={videoRef}
          src={primaryUrl}
          poster={image_url || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
          data-testid="exercise-video"
        />
        <div className="absolute top-2 left-2 text-[9px] uppercase tracking-widest font-bold bg-black/80 text-white px-1.5 py-0.5 flex items-center gap-1 pointer-events-none">
          <VideoCamera size={10} weight="bold" /> Video
        </div>
      </div>
    );
  }

  // image or gif
  return (
    <div className={`relative w-full h-full ${className}`}>
      <img
        src={primaryUrl}
        alt={exercise?.name || ""}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      {externalVideoLink && (
        <a
          href={externalVideoLink}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="absolute bottom-1.5 right-1.5 text-[9px] uppercase tracking-widest font-bold bg-black/80 hover:bg-[#FF3B30] text-white px-1.5 py-0.5 flex items-center gap-1 transition-colors"
          title="Ver técnica en YouTube"
          data-testid="external-video-link"
        >
          <Play size={10} weight="fill" /> Video
        </a>
      )}
    </div>
  );
}
